import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';
import { EventAttributesActiveStatusLabel, EventAttributesActiveStatus, IEventAttributeDetailsDTO, IAttributesDAO, TGetEventAttributesSortKeys } from '../../../../api/attributes.dao';
import { ISubWindowsService } from '../../../../infrastructure/sub-windows';
import { EventAttributeForm } from './event-attribute-form/event-attribute-form';
import { handleApiException } from '../../../global/modal/alerts-host/alert-templates';
import { ISortRequestParams, SortOrder } from '../../../../api/shared.dto';

export class EventLevelAttributesVM {
    @mobx.observable
    public isLoading: boolean = false;
    @mobx.observable
    public selectedActiveMethod: string = '';
    @mobx.observable
    private eventAttributes: IEventAttributeDetailsDTO[] = [];
    @mobx.observable
    private sortField: TGetEventAttributesSortKeys | TGetEventAttributesSortKeys[];
    @mobx.observable
    private sortOrder: SortOrder;

    constructor(
        private readonly accountId: number,
        private readonly intl: IntlShape,
        @IoC.Inject private readonly subWindowService: ISubWindowsService = IoC.Container.get(ISubWindowsService),
        @IoC.Inject private readonly attributesDAO: IAttributesDAO = IoC.Container.get(IAttributesDAO),
    ) {
        this.sortField = 'createdAt';
        this.sortOrder = SortOrder.Desc;
    }

    @mobx.action
    public async initialize(): Promise<void> {
        this.startLoading();

        try {
            const requestParams: { accountId: number; deliveryTeamMembers?: string; coordinators?: string }
                & ISortRequestParams<TGetEventAttributesSortKeys> = {
                accountId: this.accountId,
                sortField: this.sortField,
                sortOrder: this.sortOrder
            };
            const response = await this.attributesDAO.getEventAttributesList(requestParams);
            mobx.runInAction(() => {
                this.eventAttributes = response.content;
            });
        } catch (error) {
            handleApiException(error);
        } finally {
            this.stopLoading();
        }
    }

    @mobx.action
    private startLoading(): void {
        this.isLoading = true;
    }

    @mobx.action
    private stopLoading(): void {
        this.isLoading = false;
    }

    @mobx.action
    public handleActiveStatusChange = (activeStatus: string): void => {
        this.selectedActiveMethod = activeStatus;
    };

    @mobx.action
    public setSortParams = (sortField: TGetEventAttributesSortKeys, sortOrder: SortOrder) => {
        this.sortField = sortField;
        this.sortOrder = sortOrder;
        this.initialize();
    };

    public getEventAttributeStatuses = (): Promise<{ id: string; label: string }[]> => {
        return new Promise(resolve => {
            resolve(
                Object.values(EventAttributesActiveStatus).map(activeStatus => {
                    return {
                        id: activeStatus,
                        label: EventAttributesActiveStatusLabel[activeStatus]
                    };
                })
            );
        });
    };

    public get eventAttributesList(): IEventAttributeDetailsDTO[] {
        return this.eventAttributes;
    }

    public getSortField = () => {
        return this.sortField;
    };

    public getSortOrder = () => {
        return this.sortOrder;
    };

    public openCreateEventAttributeForm(): void {
        this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.attributes.event.create.title',
                defaultMessage: 'Create Event Attribute',
                description: 'Popover header text'
            }),
            componentToHost: EventAttributeForm,
            componentToHostProps: {
                accountId: this.accountId,
                apiCallSuccessCallback: () => {
                    this.initialize();
                }
            }
        });
    }

    public openEditEventAttributeForm(eventAttributeId: number): void {
        const subWindowFacade = this.subWindowService.showSubWindow({
            title: this.intl.formatMessage({
                id: 'oe.attributes.event.edit.title',
                defaultMessage: 'Edit Event Attribute',
                description: 'Popover header text'
            }),
            componentToHost: EventAttributeForm,
            componentToHostProps: {
                accountId: this.accountId,
                attributeId: eventAttributeId.toString(),
                apiCallSuccessCallback: () => {
                    this.initialize();
                },
                deleteCallback: () => {
                    subWindowFacade.close();
                    this.initialize();
                }
            }
        });
    }
}
